import {
	AppstoreOutlined,
	HomeOutlined,
	TeamOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

/** A tab within a page, addressed as a further path segment. */
export type NavTab = {
	/** Segment under the page's path, e.g. 'assignments' → /apps/assignments. */
	segment: string;
	label: string;
};

export type NavItem = {
	/** Breadcrumb segment key; '' is the index (Home) route. */
	segment: string;
	/** Absolute path used as the sidebar menu key and breadcrumb link. */
	path: string;
	label: string;
	icon: ReactNode;
	/** Tabs the page splits into, if any. The first is its default. */
	tabs?: NavTab[];
	/** Pages nested under this one that the sidebar doesn't list. */
	pages?: NavTab[];
};

/**
 * Apps' tabs, in display order. The page builds its tab bar from this and the
 * breadcrumbs take their labels from it, so the two can't drift apart. The
 * first entry is the default: /apps redirects to it.
 */
export const APPS_TABS: NavTab[] = [
	{ segment: 'assignments', label: 'App assignments' },
	{ segment: 'permissions', label: 'High level permissions' },
];

/**
 * Pages reached from the Apps page itself rather than the sidebar. Listed here
 * so their breadcrumb labels come from the same place every other page's do.
 */
export const APPS_PAGES: NavTab[] = [{ segment: 'assign', label: 'App assignment' }];

const [APP_ASSIGNMENT] = APPS_PAGES;

/** Where the Apps page's App assignment button goes; the route mirrors it. */
export const APP_ASSIGNMENT_PATH = `/apps/${APP_ASSIGNMENT.segment}`;

/** The same, for Users. */
export const USERS_PAGES: NavTab[] = [{ segment: 'assign', label: 'User assignment' }];

const [USER_ASSIGNMENT] = USERS_PAGES;

/** Where the Users page's User assignment button goes; the route mirrors it. */
export const USER_ASSIGNMENT_PATH = `/users/${USER_ASSIGNMENT.segment}`;

/**
 * Single source of truth for the app's navigable pages: the sidebar menu and
 * breadcrumb labels are both derived from this list. (Route components are
 * wired in ./index.ts, which imports the page modules.)
 */
export const navItems: NavItem[] = [
	{ segment: '', path: '/', label: 'Home', icon: <HomeOutlined /> },
	{
		segment: 'apps',
		path: '/apps',
		label: 'Apps',
		icon: <AppstoreOutlined />,
		tabs: APPS_TABS,
		pages: APPS_PAGES,
	},
	{
		segment: 'users',
		path: '/users',
		label: 'Users',
		icon: <TeamOutlined />,
		pages: USERS_PAGES,
	},
];
